import React, { useState } from 'react';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { useListsStore } from '@/store/listsStore';
import { useTemplatesStore } from '@/store/templatesStore';
import { useGoalsStore } from '@/store/goalsStore';
import { personaService } from '@/services/personaService';
import { DevTimeTab } from '@/components/debug/DevTimeTab';

export function Debug() {
  const [activeTab, setActiveTab] = useState<'aspirations' | 'lists' | 'localStorage' | 'persona' | 'devTime'>('aspirations');
  const [isExporting, setIsExporting] = useState(false);
  const [importData, setImportData] = useState('');
  
  const aspirationsStore = useAspirationsStore();
  const listsStore = useListsStore();
  const templatesStore = useTemplatesStore();
  const goalsStore = useGoalsStore();

  const getLocalStorageData = () => {
    const data: { [key: string]: any } = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        try {
          const value = localStorage.getItem(key);
          data[key] = value ? JSON.parse(value) : value;
        } catch {
          data[key] = localStorage.getItem(key);
        }
      }
    }
    return data;
  };

  const formatJSON = (obj: any) => {
    return JSON.stringify(obj, (_key, value) => {
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value;
    }, 2);
  };

  const handleResetToPersona = () => {
    if (confirm('This will reset all data to the default Dan the Developer persona. Are you sure?')) {
      personaService.resetToPersona();
    }
  };

  const handleClearAll = () => {
    if (confirm('This will delete ALL data. Are you sure?')) {
      personaService.clearAllData();
      window.location.reload();
    }
  };

  const handleExportData = () => {
    setIsExporting(true);
    const data = personaService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `godmode-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setIsExporting(false);
  };

  const handleImportData = () => {
    if (!importData.trim()) {
      alert('Please paste JSON data first');
      return;
    }

    try {
      personaService.importData(importData);
      alert('Data imported successfully! Page will reload.');
    } catch (error) {
      alert('Failed to import data: ' + error);
    }
  };

  const handleForceLoadPersona = () => {
    personaService.forceLoadPersona();
    alert('Persona data loaded manually');
    window.location.reload();
  };

  const TabButton = ({ 
    isActive, 
    onClick, 
    children 
  }: { 
    isActive: boolean; 
    onClick: () => void; 
    children: React.ReactNode;
  }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
        isActive
          ? 'bg-blue-500 text-white border-b-2 border-blue-500'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-b-2 border-transparent'
      }`}
    >
      {children}
    </button>
  );

  const JsonDisplay = ({ title, data }: { title: string; data: any }) => (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-3 text-gray-800">{title}</h3>
      <pre className=" border border-gray-200 rounded-lg p-4 overflow-auto max-h-96 text-sm">
        {formatJSON(data)}
      </pre>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">🐛 Debug Store State</h1>
        <p className="text-gray-600">View the current state of all application stores and localStorage data.</p>
      </div>

      <div className="mb-6">
        <div className="flex space-x-1 border-b border-gray-200">
          <TabButton
            isActive={activeTab === 'aspirations'}
            onClick={() => setActiveTab('aspirations')}
          >
            Aspirations Store
          </TabButton>
          <TabButton
            isActive={activeTab === 'lists'}
            onClick={() => setActiveTab('lists')}
          >
            Lists Store
          </TabButton>
          <TabButton
            isActive={activeTab === 'localStorage'}
            onClick={() => setActiveTab('localStorage')}
          >
            LocalStorage
          </TabButton>
          <TabButton
            isActive={activeTab === 'persona'}
            onClick={() => setActiveTab('persona')}
          >
            Persona Manager
          </TabButton>
          <TabButton
            isActive={activeTab === 'devTime'}
            onClick={() => setActiveTab('devTime')}
          >
            Dev Time
          </TabButton>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {activeTab === 'aspirations' && (
          <div>
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Store Summary</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total Aspirations:</span> {aspirationsStore.aspirations.length}
                </div>
                <div>
                  <span className="font-medium">Loading:</span> {aspirationsStore.isLoading ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium">Error:</span> {aspirationsStore.error || 'None'}
                </div>
              </div>
            </div>
            
            <JsonDisplay title="Aspirations Data" data={aspirationsStore.aspirations} />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <JsonDisplay title="Store State" data={{
                isLoading: aspirationsStore.isLoading,
                error: aspirationsStore.error
              }} />
              
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Available Actions</h3>
                <ul className=" border border-gray-200 rounded-lg p-4 text-sm space-y-1">
                  <li>• initializeAspirations()</li>
                  <li>• createAspiration(data)</li>
                  <li>• updateAspiration(id, data)</li>
                  <li>• deleteAspiration(id)</li>
                  <li>• reorderAspirations(reorderedAspirations)</li>
                  <li>• getAspirationById(id)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'lists' && (
          <div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">Store Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total Lists:</span> {listsStore.lists.length}
                </div>
                <div>
                  <span className="font-medium">Filtered Lists:</span> {listsStore.filteredLists.length}
                </div>
                <div>
                  <span className="font-medium">Templates:</span> {templatesStore.templates.length}
                </div>
                <div>
                  <span className="font-medium">Selected:</span> {listsStore.selectedListIds.length}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <JsonDisplay title="Lists Data" data={listsStore.lists} />
              <JsonDisplay title="Templates Data" data={templatesStore.templates} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <JsonDisplay title="Filters & Sort" data={{
                filters: listsStore.filters,
                sort: listsStore.sort
              }} />
              <JsonDisplay title="Selection State" data={{
                selectedListIds: listsStore.selectedListIds,
                selectedItemIds: listsStore.selectedItemIds
              }} />
            </div>

            <JsonDisplay title="Undo Stack" data={listsStore.undoStack} />

            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Available Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">List Management</h4>
                  <ul className=" border border-gray-200 rounded-lg p-4 text-sm space-y-1">
                    <li>• createList(data)</li>
                    <li>• updateList(id, updates)</li>
                    <li>• deleteList(id)</li>
                    <li>• reorderLists(reorderedLists)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Item Management</h4>
                  <ul className=" border border-gray-200 rounded-lg p-4 text-sm space-y-1">
                    <li>• addItem(listId, content)</li>
                    <li>• updateItem(listId, itemId, updates)</li>
                    <li>• deleteItem(listId, itemId)</li>
                    <li>• performItemBulkOperation(listId, operation)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'localStorage' && (
          <div>
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="font-semibold text-purple-900 mb-2">LocalStorage Summary</h3>
              <div className="text-sm">
                <span className="font-medium">Total Keys:</span> {localStorage.length}
              </div>
            </div>
            
            <JsonDisplay title="All LocalStorage Data" data={getLocalStorageData()} />
          </div>
        )}

        {activeTab === 'persona' && (
          <div>
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h3 className="font-semibold text-indigo-900 mb-2">👤 Persona Management</h3>
              <p className="text-sm text-indigo-700">
                Manage your app data using the Dan the Developer persona or import/export your own data.
              </p>
            </div>

            {/* Environment Status Section */}
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">🌍 Environment Status</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Environment:</span>{' '}
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                      {import.meta.env.MODE}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Development Mode:</span>{' '}
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                      {import.meta.env.DEV ? 'true' : 'false'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Mock Data Enabled:</span>{' '}
                    <span className={`font-mono px-2 py-1 rounded ${
                      personaService.isMockDataEnabled()
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {personaService.isMockDataEnabled() ? 'true' : 'false'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Has Existing Data:</span>{' '}
                    <span className={`font-mono px-2 py-1 rounded ${
                      personaService.hasPersonaData()
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {personaService.hasPersonaData() ? 'true' : 'false'}
                    </span>
                  </div>
                </div>
              </div>

              {!personaService.isMockDataEnabled() && !personaService.hasPersonaData() && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 mb-2">
                    Production mode: Mock data loading is disabled. You can manually load demo data for testing.
                  </p>
                  <button
                    onClick={handleForceLoadPersona}
                    className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition-colors"
                  >
                    Force Load Demo Data
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="p-4  border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Reset Options</h4>
                <div className="space-y-3">
                  <button
                    onClick={handleResetToPersona}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    🎭 Reset to Dan the Developer Persona
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    🗑️ Clear All Data
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Resetting to persona will load sample data for testing.
                </p>
              </div>

              <div className="p-4  border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">Export Data</h4>
                <button
                  onClick={handleExportData}
                  disabled={isExporting}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {isExporting ? '📦 Exporting...' : '💾 Export All Data as JSON'}
                </button>
                <p className="text-xs text-gray-600 mt-3">
                  Download your current data as a JSON file for backup or sharing.
                </p>
              </div>
            </div>

            <div className="p-4  border border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Import Data</h4>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Paste your JSON data here..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm font-mono"
              />
              <button
                onClick={handleImportData}
                className="mt-3 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
              >
                📥 Import Data
              </button>
              <p className="text-xs text-gray-600 mt-3">
                Import previously exported data or custom JSON data.
              </p>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold text-gray-800 mb-3">Current Persona Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Aspirations</div>
                  <div className="text-2xl font-bold text-gray-900">{aspirationsStore.aspirations.length}</div>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Goals</div>
                  <div className="text-2xl font-bold text-gray-900">{goalsStore.goals.length}</div>
                </div>
                <div className="p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Lists</div>
                  <div className="text-2xl font-bold text-gray-900">{listsStore.lists.length}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">About Dan the Developer Persona</h4>
              <p className="text-sm text-blue-800 mb-2">
                Dan the Developer is a 29-year-old Senior Software Engineer at TechCorp with 7 years of experience.
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• 5 Life Aspirations: Career, Health, Financial, Personal, Education</li>
                <li>• Multiple active goals with SMART criteria</li>
                <li>• Project lists, training schedules, and personal development tasks</li>
                <li>• Realistic progress tracking and deadlines</li>
                <li>• Weekly schedules and recurring tasks</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'devTime' && (
          <DevTimeTab />
        )}
      </div>

      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Debug Notes</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• This page is for development purposes only</li>
          <li>• Data shown here reflects the current state in memory</li>
          <li>• Changes made through the UI will be reflected here in real-time</li>
          <li>• LocalStorage data persists between sessions</li>
          <li>• Use browser dev tools console to interact with stores directly</li>
        </ul>
      </div>
    </div>
  );
}